'use client';

import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { type ActiveBreakpoints, breakpoints } from '.';

// Type definition for the responsive context during SSR or before mount.

type LoadingResponsiveContext = {
  /**
   * Whether the context provider has mounted on the client and the media queries are ready.
   */
  ready: false;

  /**
   * Map of breakpoints and their active status for min-width queries.
   *
   * IMPORTANT:
   *
   * This is `undefined` initially, as it's impossible to determine during SSR and before mount.
   * Even though it's annoying to verify if there's a value, it's necessary to prevent hydration mismatches.
   * Each component using `useMediaQueries` needs to have a default or fallback state.
   */
  minWidth: undefined;

  /**
   * Map of breakpoints and their active status for max-width queries.
   *
   * IMPORTANT:
   *
   * This is `undefined` initially, as it's impossible to determine during SSR and before mount.
   * Even though it's annoying to verify if there's a value, it's necessary to prevent hydration mismatches.
   * Each component using `useMediaQueries` needs to have a default or fallback state.
   */
  maxWidth: undefined;
};

// Type definition for the responsive context after mount.

type MountedResponsiveContext = {
  /**
   * Whether the context provider has mounted on the client and the media queries are ready.
   */
  ready: true;

  /**
   * Map of breakpoints and their active status for min-width queries.
   *
   * IMPORTANT:
   *
   * This is `undefined` initially, as it's impossible to determine during SSR and before mount.
   * Even though it's annoying to verify if there's a value, it's necessary to prevent hydration mismatches.
   * Each component using `useMediaQueries` needs to have a default or fallback state.
   */
  minWidth: ActiveBreakpoints;

  /**
   * Map of breakpoints and their active status for max-width queries.
   *
   * IMPORTANT:
   *
   * This is `undefined` initially, as it's impossible to determine during SSR and before mount.
   * Even though it's annoying to verify if there's a value, it's necessary to prevent hydration mismatches.
   * Each component using `useMediaQueries` needs to have a default or fallback state.
   */
  maxWidth: ActiveBreakpoints;
};

// Union type for the responsive context.

type ResponsiveContextType = LoadingResponsiveContext | MountedResponsiveContext;

// Default value for the responsive context.

const defaultResponsiveContext: ResponsiveContextType = {
  ready: false,
  minWidth: undefined,
  maxWidth: undefined,
};

/**
 * Context to store the active breakpoints (minWidth and maxWidth).
 *
 * @see https://reactjs.org/docs/context.html
 */

const ResponsiveContext = createContext<ResponsiveContextType>(defaultResponsiveContext);

/**
 * Custom hook to access the responsive context.
 *
 * @returns undefined or the breakpoints and their active status (minWidth and maxWidth) if mounted.
 */

export const useMediaQueries = (): ResponsiveContextType => useContext(ResponsiveContext);

/**
 * Provider to manage the responsive context and update the active breakpoints.
 */

export const ResponsiveProvider = ({ children }: { children: ReactNode }) => {
  const [ready, setReady] = useState(false);

  const isSm = useMediaQuery({ minWidth: breakpoints.sm });
  const isMd = useMediaQuery({ minWidth: breakpoints.md });
  const isLg = useMediaQuery({ minWidth: breakpoints.lg });
  const isXl = useMediaQuery({ minWidth: breakpoints.xl });

  // Set `ready` after mount to distinguish SSR from CSR.
  useEffect(() => {
    setReady(true);
  }, []);

  let contextValue: ResponsiveContextType = defaultResponsiveContext;

  if (ready) {
    contextValue = {
      ready: true,
      minWidth: { xs: true, sm: isSm, md: isMd, lg: isLg, xl: isXl },
      maxWidth: { xs: !isSm, sm: !isMd, md: !isLg, lg: !isXl, xl: true },
    };
  }

  return <ResponsiveContext value={contextValue}>{children}</ResponsiveContext>;
};

export default ResponsiveProvider;
